import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import brandCore from "../../core/brand.core";
import tagCore from "../../core/tag.core";
import { getSingleCategory } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import AdjustmentsHorizontalIcon from "../../icons/adjustmentsHorizontal.icon";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import CloseIcon from "../../icons/iconClose.comp";
import LoadingOvalIcon from "../../icons/loadingOval.icon";
import MagnifyingIcon from "../../icons/magnifying.icon";
import XMarkIcon from "../../icons/xMark.icon";
import { ProductListFilter, ProductType } from "../../types/product";
import BrandMultipleSelectorComp from "../brand/brandMultipleSelector.comp";
import CategorySelectorComp from "../category/categorySelector.comp";
import { FancyCheckbox, FancyMinMaxInput, FancyTextInputWithIcon } from "../reuseables/form.comp";
import Modal from "../reuseables/modal.comp";
import ProductStatusSpanComp from "./productStatusSpan.comp";
import TagMultipleSelectorComp from "../tag/tagMutipleSelector.comp";
import BrandName from "../brand/brandName.comp";
import AttributeName from "../attribute/attributeName.comp";
import AttributeMultipleSelectorComp from "../attribute/attributeMultipleSelector.comp";
import ProductTypeMultipleSelectorComp from "./productTypeMultipleSelector.comp";

interface DummyForm {
  search: string;
}

export default function ProductListFilterComp({ methods }: { methods: UseFormReturn<any, any> }) {
  const [modalOpened, setModalOpened] = useState(false);
  const btnRef = useRef<HTMLDivElement>(null);

  return (
    <section data-type="section">
      <FilterBar btnRef={btnRef} setModalOpened={setModalOpened} methods={methods} />
      <FilterModal btnRef={btnRef} modalOpened={modalOpened} setModalOpened={setModalOpened} methods={methods} />
    </section>
  );
}

function FilterModal({
  btnRef,
  modalOpened,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLDivElement>;
  modalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<ProductListFilter, any>;
}) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();

  const dummyMethods = useForm<DummyForm>({
    defaultValues: {
      search: methods.getValues("search"),
    },
  });

  function onSubmit(data: DummyForm) {
    methods.setValue("search", data.search);
    setModalOpened(false);

    if (methods.getValues("page") !== 1) {
      push({
        pathname: "/products",
        query: { page: 1 },
      });
    }
  }

  useEffect(() => {
    if (modalOpened) {
      dummyMethods.setFocus("search");
    }
  }, [modalOpened, dummyMethods]);

  if (!modalOpened) return null;

  return (
    <Modal
      id="product-filter-modal"
      btnRef={btnRef}
      setModalOpened={setModalOpened}
      cssHeight="top-0 md:top-1/2 h-[100dvh] md:h-[700px] "
    >
      <div className="py-14 px-8 space-y-8">
        <h1 className="basis-full text-center text-xl">{t(msg.product.productListFilter)}</h1>
        <div className="grid grid-cols-2 gap-8">
          <form onSubmit={dummyMethods.handleSubmit(onSubmit)} className="col-span-2 flex gap-2 items-center">
            <FancyTextInputWithIcon
              register={() => dummyMethods.register("search")}
              placeholder={t(msg.product.searchProduct)}
              icon={<MagnifyingIcon />}
              rightIcon={<CloseIcon />}
              rightIconRole="clear"
              onClickRightIcon={() => dummyMethods.setValue("search", "")}
              className="grow bg-admin-bg-blue"
            />
            <input type="submit" value={t(msg.form.submit)} className="h-9" />
          </form>

          <div className="col-span-2 md:col-span-1">
            <CategorySelectorComp methods={methods} name="categoryIds" labelMaxLength={20} />
          </div>

          <div className="col-span-2 md:col-span-1">
            <BrandMultipleSelectorComp methods={methods} name="brandIds" />
          </div>
          <div className="col-span-2 md:col-span-1">
            <TagMultipleSelectorComp methods={methods} name="tagId" labelMaxLength={20} />
          </div>

          <div className="col-span-2 md:col-span-1">
            <AttributeMultipleSelectorComp methods={methods} name="attributesIds" />
          </div>

          <div className="col-span-2 md:col-span-1">
            <FancyMinMaxInput
              methods={methods}
              fieldMin="rewardPayoutMin"
              fieldMax="rewardPayoutMax"
              title={t(msg.category.rewardPayout)}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <FancyMinMaxInput methods={methods} fieldMin="priceMin" fieldMax="priceMax" title={t(msg.product.price)} />
          </div>

          <div className="col-span-2">
            <ProductTypeMultipleSelectorComp methods={methods} name="productTypes" />
          </div>

          <div>
            <FancyCheckbox register={methods.register("isPromotionProduct")} value={true}>
              {t(msg.product.isPromotionProduct)}
            </FancyCheckbox>
          </div>

          <ProductStatuses methods={methods as UseFormReturn<ProductListFilter>} />
        </div>
      </div>
    </Modal>
  );
}

function ProductStatuses({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div>
      <div>{t(msg.product.productStatus)}</div>

      <div className="flex flex-col lg:flex-row gap-2">
        <FancyCheckbox register={methods.register("statuses")} value="active">
          <ProductStatusSpanComp status="active" />
        </FancyCheckbox>
        <FancyCheckbox register={methods.register("statuses")} value="draft">
          <ProductStatusSpanComp status="draft" />
        </FancyCheckbox>
        <FancyCheckbox register={methods.register("statuses")} value="private">
          <ProductStatusSpanComp status="private" />
        </FancyCheckbox>
      </div>
    </div>
  );
}

function FilterBar({
  btnRef,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLDivElement>;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<ProductListFilter, any>;
}) {
  return (
    <div
      id="product-filter"
      ref={btnRef}
      className="border rounded-lg py-2 px-4 items-center flex justify-between w-full"
      onClick={() => {
        setModalOpened(true);
      }}
    >
      <div className="flex flex-wrap gap-2">
        <SearchItem methods={methods} />
        <CategoryItems methods={methods as UseFormReturn<ProductListFilter>} />
        <BrandItems methods={methods as UseFormReturn<ProductListFilter>} />
        <AttributesItems methods={methods as UseFormReturn<ProductListFilter>} />
        <ProductTypeItems methods={methods as UseFormReturn<ProductListFilter>} />
        <PriceRangeItem methods={methods as UseFormReturn<ProductListFilter>} />
        <RewardPayoutRangeItem methods={methods as UseFormReturn<ProductListFilter>} />
        <ProductStatuesItems methods={methods as UseFormReturn<ProductListFilter>} />
        <TagItems methods={methods} />
      </div>
      <AdjustmentsHorizontalIcon className="w-6 h-6 text-her-dodgerblue" />
    </div>
  );
}
function SearchItem({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const search = useWatch({ control: methods.control, name: "search" });

  if (!search) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("search", undefined);
      }}
    >
      {search}
    </Badge>
  );
}

function CategoryItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const categoryIds = useWatch({ control: methods.control, name: "categoryIds" });

  if (!categoryIds) return null;

  return (
    <>
      {categoryIds.map((catId) => (
        <Badge
          bgColor="bg-admin-blue"
          key={catId}
          onClose={() => {
            methods.setValue(
              "categoryIds",
              categoryIds.filter((v) => v !== catId)
            );
          }}
        >
          <CategoryName catId={catId} />
        </Badge>
      ))}
    </>
  );
}

function CategoryName({ catId }: { catId: string }) {
  const {
    data: category,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["category", catId],
    queryFn: (context) => getSingleCategory(context.queryKey[1]),
  });

  if (isPending) return <LoadingOvalIcon />;
  if (isError) return <ErrorCenterIcon reason={error.message} />;

  if (!category) return null;

  return <span data-label={`category-${category.code}`}>{localeCore.getLocaleValueDefaultFallback(category.name)}</span>;
}

function BrandItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const brandIds = useWatch({ control: methods.control, name: "brandIds" });

  if (!brandIds) return null;

  return (
    <>
      {brandIds.map((brandId) => (
        <Badge
          bgColor="bg-admin-blue"
          key={brandId}
          onClose={() => {
            methods.setValue(
              "brandIds",
              brandIds.filter((v) => v !== brandId)
            );
          }}
        >
          <BrandName brandId={brandId} />
        </Badge>
      ))}
    </>
  );
}

function AttributesItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const attributesIds = useWatch({ control: methods.control, name: "attributesIds" });

  if (!attributesIds) return null;

  return (
    <>
      {attributesIds.map((attrId) => (
        <Badge
          bgColor="bg-admin-blue"
          key={attrId}
          onClose={() => {
            methods.setValue(
              "attributesIds",
              attributesIds.filter((v) => v !== attrId)
            );
          }}
        >
          <AttributeName attrId={attrId} />
        </Badge>
      ))}
    </>
  );
}

function PriceRangeItem({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { t, msg } = useTranslation();
  const [priceMin, priceMax] = useWatch({ control: methods.control, name: ["priceMin", "priceMax"] });

  if (!priceMin && !priceMax) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("priceMin", undefined);
        methods.setValue("priceMax", undefined);
      }}
    >
      <span data-label={`price-${priceMin}-${priceMax}`}>
        {t(msg.product.price)}: ${priceMin} - ${priceMax}
      </span>
    </Badge>
  );
}

function RewardPayoutRangeItem({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { t, msg } = useTranslation();
  const [rewardPayoutMin, rewardPayoutMax] = useWatch({ control: methods.control, name: ["rewardPayoutMin", "rewardPayoutMax"] });

  if (!rewardPayoutMin && !rewardPayoutMax) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("rewardPayoutMin", undefined);
        methods.setValue("rewardPayoutMax", undefined);
      }}
    >
      <span data-label={`rewardPayout-${rewardPayoutMin}-${rewardPayoutMax}`}>
        {t(msg.category.rewardPayout)}: ${rewardPayoutMin} - ${rewardPayoutMax}
      </span>
    </Badge>
  );
}

function ProductStatuesItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const statuses = useWatch({ control: methods.control, name: "statuses" });

  if (!statuses) return null;

  return (
    <>
      {statuses.map((status) => (
        <Badge
          bgColor="bg-admin-blue"
          key={status}
          onClose={() => {
            methods.setValue(
              "statuses",
              statuses.filter((v) => v !== status)
            );
          }}
        >
          <ProductStatusSpanComp status={status} />
        </Badge>
      ))}
    </>
  );
}

function Badge({ bgColor, children, onClose }: { bgColor: string; children: React.ReactNode; onClose?: () => void }) {
  return (
    <div className={`${bgColor} text-white px-4 py-2 rounded-full inline-flex gap-2`}>
      {children}
      {onClose && (
        <button
          className="close-btn fill-white"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

function TagItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const tagId = useWatch({ control: methods.control, name: "tagId" });

  if (!tagId) return null;

  return (
    <>
      {tagId.map((tagid) => (
        <Badge
          bgColor="bg-admin-blue"
          key={tagid}
          onClose={() => {
            methods.setValue(
              "tagId",
              tagId.filter((v) => v !== tagid)
            );
          }}
        >
          <TagName tagid={tagid} />
        </Badge>
      ))}
    </>
  );
}

function TagName({ tagid }: { tagid: string }) {
  const {
    data: tag,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["tag", tagid],
    queryFn: (context) => tagCore.getTag(context.queryKey[1]),
  });

  if (isPending) return <LoadingOvalIcon />;
  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (!tag) return null;

  return <span data-label={`Tag-${tag.id}`}>{localeCore.getLocaleValueDefaultFallback(tag.name)}</span>;
}

function ProductTypeItems({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const productTypes = useWatch({ control: methods.control, name: "productTypes" });

  if (!productTypes || productTypes.length === 0) return null;

  return (
    <>
      {productTypes.map((type) => (
        <Badge
          bgColor="bg-admin-blue"
          key={type}
          onClose={() => {
            methods.setValue(
              "productTypes",
              productTypes.filter((v) => v !== type)
            );
          }}
        >
          <ProductTypeName productType={type} />
        </Badge>
      ))}
    </>
  );
}

function ProductTypeName({ productType }: { productType: ProductType }) {
  const { t, msg } = useTranslation();

  const typeLabels: Record<ProductType, string> = {
    simple: t(msg.product.simple),
    variationParent: t(msg.product.variationParent),
    variationChild: "Variation Product (Child)",
    custom: t(msg.product.custom),
    bundle: t(msg.product.bundleProducts),
    giftCard: t(msg.product.giftCard),
    recycle: t(msg.recycle.recycle),
  };

  return <span data-label={`product-type-${productType}`}>{typeLabels[productType]}</span>;
}
